import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api, useSeo } from "@/lib/api";
import CouponCard from "@/components/CouponCard";
import StoreCard from "@/components/StoreCard";

export default function Search() {
  const [params] = useSearchParams();
  const q = params.get("q") || "";
  const [results, setResults] = useState({ stores: [], coupons: [], posts: [] });
  useSeo(`Search: ${q} — SharePromos`, `Search results for "${q}" — verified coupons, stores, and savings articles.`, {
    keywords: `${q}, ${q} coupon, ${q} promo code, ${q} discount, coupons for ${q}`,
  });

  useEffect(() => {
    if (q) api.get("/search", { params: { q } }).then((r) => setResults(r.data));
  }, [q]);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16" data-testid="search-page">
      <p className="overline mb-3">Search results</p>
      <h1 className="font-display text-4xl md:text-5xl tracking-tighter uppercase mb-12">&ldquo;{q}&rdquo;</h1>

      {results.stores.length > 0 && (
        <section className="mb-12">
          <h2 className="font-display text-2xl tracking-tight mb-6">Stores ({results.stores.length})</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {results.stores.map((s) => <StoreCard key={s.slug} store={s} />)}
          </div>
        </section>
      )}

      {results.coupons.length > 0 && (
        <section className="mb-12">
          <h2 className="font-display text-2xl tracking-tight mb-6">Coupons ({results.coupons.length})</h2>
          <div className="grid grid-cols-1 gap-3">
            {results.coupons.map((c) => <CouponCard key={c.id} coupon={c} />)}
          </div>
        </section>
      )}

      {results.posts.length > 0 && (
        <section className="mb-12">
          <h2 className="font-display text-2xl tracking-tight mb-6">Articles ({results.posts.length})</h2>
          <ul className="space-y-3">
            {results.posts.map((p) => (
              <li key={p.slug}>
                <Link to={`/blog/${p.slug}`} className="block border border-slate-200 hover-lift p-5 rounded-sm">
                  <p className="font-display text-lg">{p.title}</p>
                  <p className="text-sm text-slate-600 mt-1">{p.excerpt}</p>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      {results.stores.length === 0 && results.coupons.length === 0 && results.posts.length === 0 && (
        <p className="text-slate-500" data-testid="no-search-results">No results found for &ldquo;{q}&rdquo;.</p>
      )}
    </div>
  );
}
